import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { LngLat } from "mapbox-gl";
import { Utils } from "mapbox-gl-draw-snap-mode";

import { getFeatureTarget } from "./helper";

const { snap, createSnapList } = Utils;

const {
  modes,
  lib: {
    StringSet,
    CommonSelectors: { isFeature, isActiveFeature, isShiftDown },
    moveFeatures,
  },
} = MapboxDraw;

interface DrawCustomModeState {
  dragMoving: boolean;
  initialDragPanState: boolean;
  dragMoveLocation: LngLat;
  snapList: mapboxgl.MapboxGeoJSONFeature[];
  vertices: mapboxgl.MapboxGeoJSONFeature[];
  delta: LngLat;
}

interface DrawCustomModeExtension<S> extends DrawCustomMode<S> {
  dragMove(this: DrawCustomModeThis, state: S, event: MapboxDraw.MapMouseEvent): void;
  getUniqueIds(this: DrawCustomModeThis, allFeatures: mapboxgl.MapboxGeoJSONFeature[]): string[];
}

const simpleSelect: DrawCustomModeExtension<DrawCustomModeState> = {
  ...modes.simple_select,

  onSetup: function (opts) {
    const state = modes.simple_select.onSetup?.call(this, opts);
    state.options = this._ctx.options;
    state.map = this.map;
    state.delta = { lng: 0, lat: 0 };

    return state;
  },

  onClick: function (state, event) {
    // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
    event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);
    modes.simple_select.onClick?.call(this, state, event);
  },

  /**
   * We customise the onMouseDown event to allow the user to drag an unselected
   * Feature without first selecting it & to provide snapping while in this mode.
   */
  onMouseDown: function (state, event) {
    state.initialDragPanState = this.map.dragPan.isEnabled();

    if (isFeature(event)) {
      // @ts-expect-error featureTarget is possibly undefined & needs to allow for that
      event.featureTarget = getFeatureTarget.call(this, event, this._ctx.options);
      if (!event.featureTarget) {
        return;
      }

      const featureId = event.featureTarget.properties?.id;
      const feature = this.getFeature(featureId);

      // Calculate the features available for snapping
      const [snapList, vertices] = createSnapList(this.map, this._ctx.api, feature, this._ctx.options.snapOptions?.snapGetFeatures);
      state.snapList = snapList;
      state.vertices = vertices;
      state.delta = new LngLat(0, 0);

      if (feature.type === "LineString") {
        const nearestPoint = nearestPointOnLine(feature, [event.lngLat.lng, event.lngLat.lat]);
        state.delta = new LngLat(event.lngLat.lng - nearestPoint.geometry.coordinates[0], event.lngLat.lat - nearestPoint.geometry.coordinates[1]);
      } else if (feature.type === "Point") {
        state.delta = new LngLat(event.lngLat.lng - feature.getCoordinates()[0], event.lngLat.lat - feature.getCoordinates()[1]);
      }

      // If the feature is not currently selected we manually click on
      // the feature to select it, allowing for the user to click and drag in one action
      if (!isActiveFeature(event) && !isShiftDown(event)) {
        this.clickOnFeature?.(state, event);
        this.startOnActiveFeature?.(state, event);
      }
    }

    modes.simple_select.onMouseDown?.call(this, state, event);
  },

  /**
   * We customize this to only select editingLayerIds features if a layerId is provided
   */
  getUniqueIds: function (allFeatures) {
    if (!this._ctx.options.editingLayerIds?.length) {
      return modes.simple_select.getUniqueIds?.call(this, allFeatures) || [];
    }

    if (!allFeatures.length) return [];
    const ids = allFeatures
      .filter((f) => this._ctx.options.editingLayerIds?.includes(f.properties?.user_layerId))
      .map((s) => s.properties?.id)
      .filter((id) => id !== undefined)
      .reduce((memo, id) => {
        memo.add(id);
        return memo;
      }, StringSet());

    return ids.values();
  },

  /**
   * We customise the dragMove function to allow for points to snap.
   */
  dragMove: function (state, event) {
    state.dragMoving = true;
    event.originalEvent.stopPropagation();

    const { lng, lat } = snap(state, event);
    // The event lngLat is mutated to include the delta, which is the distance
    // from the actual feature the user clicked. This allows us to snap to the
    // correct location. Without this the snapping would be off by the offset
    // of the initial click
    event.lngLat = new LngLat(lng + state.delta.lng, lat + state.delta.lat);
    const delta = {
      lng: event.lngLat.lng - state.dragMoveLocation.lng,
      lat: event.lngLat.lat - state.dragMoveLocation.lat,
    };

    moveFeatures(this.getSelected(), delta);
    state.dragMoveLocation = event.lngLat;
  },
};

export default simpleSelect;
